import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Grid,
  Tabs,
  Tab,
  Box,
  Typography,
  Stack,
  Autocomplete,
  Tooltip,
  TextField,
  CircularProgress,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFRadioGroup,
  RHFTextField,
} from "../../../../../RHFComponents/RHFComponents";
import { MedintDataContext } from "../../../../Context/MedintContextWrapper";
import { StyledDataGrid } from "../../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";
import { GridColDef } from "@mui/x-data-grid";
import { ReactComponent as CopyFromDoctorIcon } from "../../../../../../assets/icons/MedintCopyFromDoctorIcon.svg";
import { ReactComponent as AddIcon } from "../../../../../../assets/icons/MedintAddIcon.svg";
import { ReactComponent as ModifyIcon } from "../../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/icons/MedintDeleteIcon.svg";
import {
  FormattedMedPreslDataObject,
  FormattedRagggpDataObject,
  PayloadRicAnaPre,
  ResponseRicAnaPre,
} from "../../../../../../services/medintInterfaces";
import MedPresl from "../MedPreslModal/MedPresl";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AllertMessage } from "../../MedintDetails";
import {
  generalResearch,
  medPrest,
  medPresl,
  medprstgps,
  ricMedprstgpm,
  ricMedPrstGpd,
  ricPrestgpd,
  ricAnapres,
  ricPrestgpm,
} from "../../../../../../networking";
import SospensioneModal from "./SospensioneModal";
import AttributiGpModal from "./AttributiGpModal";

dayjs.extend(customParseFormat);

//Type per i valori del form
type MedPrestFormValues = {
  medico: string;
  codiceSpecia: string;
  codiceAnapre: string;
  codiceTpgpre: string;
  durataMin: string;
  attesaMin: string;
  nMaxPrest: string;
  minutiInizio: string;
  dataFineValidita: string | Dayjs | null;
  nota1: any;
  nota2: any;
  codPgmedt: string;
  idPgmedt: string;
  etaMin: string;
  etaMax: string;
  genere: "" | "M" | "F" | "N";
  codiceRaggruppamento: string;
  flagAccettazione: boolean;
  flagRaggruppabile: boolean;
  flagEscludiGP: boolean;
  tempoPreparazione: string;
  codMedintSost: string;
  codAnapreSost: string;
  datiSospensione: MedPrestDatiSospensione | null;
  specialita?: string;
  tipologia?: string;
  prestazione?: string;
  idAnapre?: string;
};

type MedPrestDatiSospensione = {
  sospesa?: boolean;
  dataSospensione?: string | Dayjs | null;
  note?: string;
};

//Type per i dati che arrivano dall'api medprest
type MedPrestApiRow = {
  MINUTIINIZIO: string;
  CODICESPECIA?: string | null;
  CODICEANAPRE?: string | null;
  CODICETPGPRE?: string | null;
  IDANAPRE?: string | null;
  DURATAMIN?: string | number | null;
  ATTESAMIN?: string | number | null;
  NMAXPREST?: string | number | null;
  DATAFINEVAL?: string | null;
  FLAGACCETTAZIONE?: any;
  NOTA1?: string | null;
  NOTA2?: string | null;
  CODANAPRESOST?: string | null;
  CODMEDINTSOST?: string | null;
  ETAMIN?: string | number | null;
  ETAMAX?: string | number | null;
  GENERE?: string | null;
  FLAGRAGGRUPPABILE?: any;
  CODICERAGGRUPPAMENTO?: string | null;
  FLAGESCLUDIGP?: any;
  TEMPOPREPARAZIONE?: string | number | null;
  DESCRANAPRE?: string;
  DESCRSPECIALITA?: string;
  DESCRTPGPRE?: string;
};

type MedPrestProps = {
  open: boolean;
  mode: "create" | "edit" | "copy";
  //Contiene i valori principali della tabella, tra cui idMedprest
  row?: any;
  onClose: () => void;
  onConfirm?: (data: MedPrestFormValues) => void;
};

export type MedPreslFormValues = {
  idMedpresl: string;
  crud: string;
  idMedprest: string;
  dataIniValidita: Dayjs;
  importoListino: string;
  importoPaz: string;
  importoEnte: string;
};

export type MedPrestDistr = {
  crud: string;
  codiceSiss?: string | null;
  distretto?: string | null;
  opLogico?: string;
  dataIni: Dayjs | string | null;
  dataFin: Dayjs | string | null;
  abbDisatt: boolean;
  prgintDistr?: string;
};

export type MedPrestMetodiche = {
  crud: string;
  codiceSiss?: string | null;
  metodica?: string | null;
  dataIni?: Dayjs | string | null;
  dataFin?: Dayjs | string | null;
  abbDisatt: boolean;
  prgintMetod?: string;
};

type RigaImporto = {
  id: number;
  inizio: string;
  listino: string;
  paziente: string;
  ente: string;
};

//Tipo per gestire i dati che arrivano dalla tabella pgmedt
type PgmedtRow = {
  pkey: string;
  "cod.programma": string;
  descrizione?: string;
};

//Tipo per gestire le metodiche associate
export type MetodAssRow = {
  id: number;
  MEDPRSTGPM_PRGINT: string;
  MEDPREST_PRGINT: string;
  ANAPRES_SISS_COD: string;
  GPMETOD_CODICE: string;
  MEDPRSTGPM_SW_DISABILITATO: string;
  MEDPRSTGPM_PID_ACU: string;
  MEDPRSTGPM_TIMESTAMP: string;
  MEDPRSTGPM_DT_INI_VAL: string;
  MEDPRSTGPM_DT_FINE_VAL: string;
  DECODE_GPMETOD_GPMETOD_DESCRIZIONE: string;
  DECODE_MEDPREST_SPECIA_CODICE: string;
  DECODE_MEDPREST_ANAPRE_COD: string;
  DECODE_ANAPRES_ANAPRES_SISS_DESCR: string;
};

//Tipo per gestire i metodiche di default
export type MetodDefault = {
  id: number;
  GPMETOD_CODICE: string;
  DECODE_GPMETOD_GPMETOD_DESCRIZIONE: string;
};

//Tipo per gestire i distretti associati
export type DistrAssRow = {
  id: number;
  MEDPRSTGPD_PRGINT: string;
  MEDPREST_PRGINT: string;
  ANAPRES_SISS_COD: string;
  GPDISTR_CODICE: string;
  MEDPRSTGPD_SW_DISABILITATO: string;
  MEDPRSTGPD_PID_ACU: string;
  MEDPRSTGPD_TIMESTAMP: string;
  MEDPRSTGPD_OP_LOGICO: string;
  MEDPRSTGPD_DT_INI_VAL: string;
  MEDPRSTGPD_DT_FINE_VAL: string;
  DECODE_GPDISTR_GPDISTR_DESCRIZIONE: string;
  DECODE_MEDPREST_SPECIA_CODICE: string;
  DECODE_MEDPREST_ANAPRE_COD: string;
  DECODE_ANAPRES_ANAPRES_SISS_DESCR: string;
};

//Tipo per gestire i distretti di default
export type DistrDefaultRow = {
  id: number;
  GPDISTR_CODICE: string;
  PRESTGPD_OP_LOGICO: string;
  DECODE_GPDISTR_GPDISTR_DESCRIZIONE: string;
  DECODE_ANAPRES_ANAPRES_SISS_COD: string;
  DECODE_ANAPRES_SPECIA_CODICE: string;
  DECODE_ANAPRES_ANAPRE_COD: string;
  DECODE_ANAPRE_ANAPRE_DESCR: string;
};

//Tipo per gestire i distretti di default
export type MethodsDefaultRow = {
  id: number;
  GPMETOD_CODICE: string;
  DECODE_GPMETOD_GPMETOD_DESCRIZIONE: string;
  DECODE_ANAPRES_ANAPRES_SISS_COD: string;
  DECODE_ANAPRES_SPECIA_CODICE: string;
  DECODE_ANAPRES_ANAPRE_COD: string;
};

//Funzioni per convertire i booleani delle checkbox in stringa "0" o "1" come richiesto dall'api
export const b01 = (v: boolean | undefined | null): "0" | "1" =>
  v ? "1" : "0";
export const asBool = (v: any): boolean => String(v ?? "0") === "1";

//Funzione per mappare i valori da api a form
const mapApiToForm = (
  api: MedPrestApiRow,
  medico: string,
): Partial<MedPrestFormValues> => ({
  medico,
  codiceSpecia: api.CODICESPECIA ?? "",
  codiceAnapre: api.CODICEANAPRE ?? "",
  codiceTpgpre: api.CODICETPGPRE ?? "",
  durataMin: String(api.DURATAMIN ?? "0"),
  attesaMin: String(api.ATTESAMIN ?? "0"),
  nMaxPrest: String(api.NMAXPREST ?? "0"),
  minutiInizio: String(api.MINUTIINIZIO ?? "0"),
  dataFineValidita: api.DATAFINEVAL ?? "9999-12-31",
  flagAccettazione: asBool(api.FLAGACCETTAZIONE),
  flagRaggruppabile: asBool(api.FLAGRAGGRUPPABILE),
  flagEscludiGP: asBool(api.FLAGESCLUDIGP),
  nota1: api.NOTA1 ?? "",
  nota2: api.NOTA2 ?? "",
  codMedintSost: api.CODMEDINTSOST ?? "",
  codAnapreSost: api.CODANAPRESOST ?? "",
  etaMin: String(api.ETAMIN ?? "0"),
  etaMax: String(api.ETAMAX ?? "0"),
  genere: (api.GENERE ?? "") as any,
  codiceRaggruppamento: api.CODICERAGGRUPPAMENTO ?? "",
  tempoPreparazione: String(api.TEMPOPREPARAZIONE ?? "0"),
  tipologia: String(api.DESCRTPGPRE ?? "N/a DescrTip"),
  specialita: String(api.DESCRSPECIALITA ?? "N/a DescrSpec"),
  prestazione: String(api.DESCRANAPRE ?? "N/a DescrPrest"),
  idAnapre: String(api.IDANAPRE ?? ""),
});

const MedPrest = ({ open, mode, row, onClose, onConfirm }: MedPrestProps) => {
  const context = useContext(MedintDataContext);
  // States per l'allert
  const [allert, setAllert] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<string>("warning");

  const [loading, setLoading] = useState<boolean>(false);

  const methods = useForm<MedPrestFormValues>({
    defaultValues: {
      medico: "",
      codiceSpecia: "",
      codiceAnapre: "",
      codiceTpgpre: "",
      durataMin: "0",
      attesaMin: "0",
      nMaxPrest: "0",
      minutiInizio: "0",
      dataFineValidita: "",
      flagAccettazione: false,
      flagRaggruppabile: false,
      flagEscludiGP: false,
      nota1: "",
      nota2: "",
      codPgmedt: "",
      idPgmedt: "0",
      etaMin: "0",
      etaMax: "0",
      genere: "",
      codiceRaggruppamento: "",
      tempoPreparazione: "0",
      codMedintSost: "",
      codAnapreSost: "",
    },
  });

  const methodsMedPresSosp = useForm<MedPrestDatiSospensione>({
    defaultValues: {
      sospesa: false,
      dataSospensione: dayjs(),
      note: "",
    },
  });

  const methodsMedPresl = useForm<MedPreslFormValues>({
    defaultValues: {
      crud: "r",
      dataIniValidita: dayjs(),
      idMedpresl: "",
      idMedprest: "",
      importoListino: "",
      importoPaz: "",
      importoEnte: "",
    },
  });

  const methodsDistr = useForm<MedPrestDistr>({
    defaultValues: {
      crud: "r",
      codiceSiss: null,
      distretto: null,
      opLogico: "U",
      dataIni: null,
      dataFin: null,
      abbDisatt: false,
    },
  });

  const methodsMetod = useForm<MedPrestMetodiche>({
    defaultValues: {
      crud: "r",
      codiceSiss: null,
      metodica: null,
      dataIni: null,
      dataFin: null,
      abbDisatt: false,
    },
  });

  const prestazioniCols: GridColDef[] = [
    { field: "inizio", headerName: "Inizio", flex: 1 },
    {
      field: "listino",
      headerName: "Listino",
      flex: 1,
      renderCell: (value: any) => {
        return `${value.row.listino} €`;
      },
    },
    {
      field: "paziente",
      headerName: "Paziente",
      flex: 1,
      renderCell: (value: any) => {
        return `${value.row.paziente} €`;
      },
    },
    {
      field: "ente",
      headerName: "Ente",
      flex: 1,
      renderCell: (value: any) => {
        return `${value.row.ente} €`;
      },
    },
  ];

  const BLUE = "#003267";
  const [tab, setTab] = React.useState<"dettaglio" | "mosa">("dettaglio");
  const [showMedpreslModal, setShowMedpreslModal] =
    React.useState<boolean>(false);
  const [showMedpresSospensioniModal, setShowMedpresSospensioniModal] =
    React.useState<boolean>(false);
  const [sospesaFound, setSospesaFound] = React.useState<boolean>(false);
  const [showMedpresDistretto, setShowMedpresDistretto] =
    React.useState<boolean>(false);
  const [selectedImporto, setSelectedImporto] = useState<any>(undefined);
  //Stato per salvare e gestire i codici di pgmedt ricevuti da generalResearch
  const [listPgmedt, setListPgmedt] = useState<PgmedtRow[]>([]);
  const [listDistAssoc, setListDistAssoc] = useState<DistrAssRow[] | null>(
    null,
  );
  const [listMetodAss, setListMetodAss] = useState<MetodAssRow[] | null>(null);
  const [listDistDefault, setListDistDefault] = useState<
    DistrDefaultRow[] | null
  >(null);
  const [listMetodDefault, setListMetodDefault] = useState<
    MethodsDefaultRow[] | null
  >(null);

  //State per definire e sfruttare la funzione di cancellazione necessaria alla conferma dell'alert in fase di cancellazione
  const [fn, setFn] = useState<(() => void) | null>(null);

  // 1° click: valida il form e poi chiede conferma
  const askConfirmMedPrest = () => {
    methods.handleSubmit(() => {
      setMessage("Confermare il salvataggio della prestazione?");
      setSeverity("warning");
      setAllert(true);
    })();
  };

  // 2° click (dentro alert): submit
  const onAlertConfirm = async () => {
    try {
      await methods.handleSubmit(handleSubmit)();
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Si è verificato un errore durante il salvataggio.");
      setAllert(true);
    }
  };

  // auto-close se non è warning
  useEffect(() => {
    if (allert && severity !== "warning") {
      const t1 = setTimeout(() => setAllert(false), 4000);
      const t2 = setTimeout(() => {
        setSeverity("warning");
        setMessage("");
      }, 6000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [allert, severity]);

  const raggruppabile = useWatch({
    control: methods.control,
    name: "flagRaggruppabile",
  });

  //Funzione per inviare la modifica/creazione dei dati nella tabella
  const handleSubmit = async (data: MedPrestFormValues) => {
    const payload = {
      crud: mode === "edit" ? "u" : "c",
      idMedint: context?.doctorAgenda?.medico?.[0]?.IDMEDICO ?? "",
      //Se edito, devo passare anche l'idMedprest, altrimenti non lo passo
      ...(mode === "edit" ? { idMedprest: row?.IdMedPrest } : {}),
      prestazioni: [
        {
          codiceSpecia: data.codiceSpecia,
          codiceAnapre: data.codiceAnapre,
          codiceTpgpre: data.codiceTpgpre,
          durataMin: data.durataMin,
          attesaMin: data.attesaMin,
          nMaxPrest: data.nMaxPrest,
          minutiInizio: data.minutiInizio,
          dataFineValidita:
            typeof data.dataFineValidita === "string"
              ? data.dataFineValidita
              : (data.dataFineValidita?.format?.("YYYY-MM-DD") ?? "9999-12-31"),
          flagAccettazione: b01(data.flagAccettazione),
          flagRaggruppabile: b01(data.flagRaggruppabile),
          flagEscludiGP: b01(data.flagEscludiGP),
          nota1: data.nota1.codice,
          nota2: data.nota2.codice,
          codPgmedt: data.codPgmedt,
          idPgmedt: data.idPgmedt,
          etaMin: data.etaMin,
          etaMax: data.etaMax,
          genere: data.genere,
          codiceRaggruppamento: data.codiceRaggruppamento,
          tempoPreparazione: data.tempoPreparazione,
          codMedintSost: data.codMedintSost,
          codAnapreSost: data.codAnapreSost,
        },
      ],
    };
    let response: any;
    try {
      response = await medPrest(payload as any);

      console.log({ response });

      if (response?.data[0]?.esito === "OK") {
        setSeverity("success");
        setMessage(response?.data?.messaggio ?? "Operazione completata.");
      } else {
        setSeverity("error");
        setMessage(response?.data?.messaggio ?? "Operazione non riuscita.");
      }
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore di comunicazione con il server.");
      setAllert(true);
    } finally {
      setAllert(true);
      setTimeout(() => {
        onConfirm?.(response as any);
      }, 2000);
    }
  };

  // Funzione per ottenere la specialità da una prestazione
  const getSpecialitaFromPrestazione = (prestazione: any): string =>
    String(
      prestazione?.SPECIALITA ??
        prestazione?.CODICESPECIA ??
        prestazione?.CODSPECIA ??
        "",
    ).trim();

  // Prestazioni mostrate in base alla specialità selezionata (solo in create)
  const [
    prestazioniFiltratePerSpecialita,
    setPrestazioniFiltratePerSpecialita,
  ] = useState<any[]>(() => context?.performancePRESTAZIONI ?? []);

  // Watch dei 2 campi collegati
  const specialitaSelezionata = useWatch({
    control: methods.control,
    name: "codiceSpecia",
  });
  const prestazioneSelezionata = useWatch({
    control: methods.control,
    name: "codiceAnapre",
  });

  // 1) Se cambio SPECIALITÀ filtro le PRESTAZIONI e resetto la prestazione se non è più coerente
  useEffect(() => {
    if (mode !== "create") return;

    const tutteLePrestazioni = context?.performancePRESTAZIONI ?? [];
    const codiceSpecialita = String(specialitaSelezionata ?? "").trim();

    // Nessuna specialità: mostro tutto
    if (!codiceSpecialita) {
      setPrestazioniFiltratePerSpecialita(tutteLePrestazioni);
      return;
    }

    // Mostro solo prestazioni compatibili con la specialità scelta
    const prestazioniCompatibili = tutteLePrestazioni.filter(
      (p: any) => getSpecialitaFromPrestazione(p) === codiceSpecialita,
    );
    setPrestazioniFiltratePerSpecialita(prestazioniCompatibili);

    // Se la prestazione attuale non è tra quelle compatibili → la resetto
    if (prestazioneSelezionata) {
      const prestazioneAncoraValida = prestazioniCompatibili.some(
        (p: any) =>
          String(p?.PRESTAZIONE ?? "").trim() ===
          String(prestazioneSelezionata).trim(),
      );

      if (!prestazioneAncoraValida) methods.setValue("codiceAnapre", "");
    }
  }, [
    specialitaSelezionata,
    prestazioneSelezionata,
    mode,
    context?.performancePRESTAZIONI,
    methods,
  ]);

  // 2) Se cambio PRESTAZIONE imposto automaticamente la SPECIALITÀ coerente
  useEffect(() => {
    if (mode !== "create") return;

    const tutteLePrestazioni = context?.performancePRESTAZIONI ?? [];
    const codicePrestazione = String(prestazioneSelezionata ?? "").trim();
    if (!codicePrestazione) return;

    const prestazione = tutteLePrestazioni.find(
      (p: any) => String(p?.PRESTAZIONE ?? "").trim() === codicePrestazione,
    );
    if (!prestazione) return;

    const specialitaDellaPrestazione =
      getSpecialitaFromPrestazione(prestazione);
    if (!specialitaDellaPrestazione) return;

    // Evito loop: setto solo se è diversa
    if (
      String(specialitaSelezionata ?? "").trim() !== specialitaDellaPrestazione
    ) {
      methods.setValue("codiceSpecia", specialitaDellaPrestazione);
    }
  }, [
    prestazioneSelezionata,
    specialitaSelezionata,
    mode,
    context?.performancePRESTAZIONI,
    methods,
  ]);

  const rowsImporti = useMemo<RigaImporto[] | undefined>(() => {
    if (!Array.isArray(context?.importiMEDPRESL)) return [];

    return context?.importiMEDPRESL?.map((row: any, index: number) => ({
      id: index,
      idMedPresl: row?.IDMEDPRESL || "",
      inizio: dayjs(row?.DATAINIZIOLISTINO).format("DD/MM/YYYY") || "",
      listino: row?.IMPORTOTOT || "0",
      paziente: row?.IMPORTOPAZ || "0",
      ente: row?.IMPORTOENTE || "0",
    }));
  }, [context?.importiMEDPRESL]);

  const handlePayloadMEDPRESL = async () => {
    const tempObj: FormattedMedPreslDataObject = {
      crud: methodsMedPresl.getValues("crud") ?? "",
      dataIniValidita:
        dayjs(methodsMedPresl.getValues("dataIniValidita")).format(
          "YYYY-MM-DD",
        ) ?? "",
      idMedprest: row?.IdMedPrest ?? "",
      idMedpresl: methodsMedPresl.getValues("idMedpresl") ?? "",
      importoListino: methodsMedPresl.getValues("importoListino") ?? "",
      importoEnte: methodsMedPresl.getValues("importoEnte") ?? "",
      importoPaz: methodsMedPresl.getValues("importoPaz") ?? "",
    };

    try {
      await medPresl(tempObj).then((response: any) => {
        setAllert(true);
        setMessage(response.data.messaggio);
        if (response.data.esito === "OK") {
          setSeverity("success");
        } else {
          setSeverity("error");
        }
      });
    } catch (error) {
      console.error("Errore sull'invio del payload di medpresl:", error);
    } finally {
      setTimeout(() => {
        setAllert(false);
      }, 4000);
    }
  };

  const handlePayloadSospensione = async () => {
    const tempObj: FormattedMedPreslDataObject = {
      crud: methodsMedPresl.getValues("crud") ?? "",
      dataIniValidita:
        dayjs(methodsMedPresl.getValues("dataIniValidita")).format(
          "YYYY-MM-DD",
        ) ?? "",
      idMedprest: row?.IdMedPrest ?? "",
      idMedpresl: methodsMedPresl.getValues("idMedpresl") ?? "",
      importoListino: methodsMedPresl.getValues("importoListino") ?? "",
      importoEnte: methodsMedPresl.getValues("importoEnte") ?? "",
      importoPaz: methodsMedPresl.getValues("importoPaz") ?? "",
    };

    try {
      await medPresl(tempObj).then((response: any) => {
        setAllert(true);
        setMessage(response.data.messaggio);
        if (response.data.esito === "OK") {
          setSeverity("success");
        } else {
          setSeverity("error");
        }
      });
    } catch (error) {
      console.error("Errore sull'invio del payload di medpresl:", error);
    } finally {
      setTimeout(() => {
        setAllert(false);
      }, 4000);
    }
  };

  useEffect(() => {
    if (!allert) {
      // TEST per implementazione previo merge dei dati sulla tabella
      const defaultPayload: FormattedMedPreslDataObject = {
        crud: "r",
        idMedpresl: "",
        idMedprest: row?.IdMedPrest,
      };

      context?.requestImportiMEDPRESL(defaultPayload);

      methodsMedPresl.reset({ crud: "r" });
    }
  }, [allert]);

  //All'apertura della schermata, recupero medPrest (Se siamo in edit, recupero la riga corrispondente) e pgmedt
  useEffect(() => {
    if (!open) return;

    const medicoBase = context?.doctorAgenda?.medico?.[0];
    const medicoFullName = medicoBase
      ? `${medicoBase.COGNOMEMEDICO ?? ""} ${medicoBase.NOMEMEDICO ?? ""}`.trim()
      : "";

    //A prescindere, ci serve pgmedt, che ricaviamo dall'api ricerche
    const getResearchPgmedt = async () => {
      try {
        const response = await generalResearch({
          tabella: "PGMEDT",
          filtro: context?.doctorAgenda?.medico?.[0]?.CODICEMEDICO ?? "",
          codice: "",
          attivi: "0",
          applicazione: "",
        });
        console.log({ response });

        setListPgmedt((response?.data?.dati ?? []) as PgmedtRow[]);
      } catch (error) {
        console.error("Error fetching PGMEDT:", error);
      }
    };

    getResearchPgmedt();

    // Se siamo in modalità create, restituisco solo il medico
    if (mode === "create") {
      methods.reset({ ...methods.getValues(), medico: medicoFullName });
      return;
    }

    //Se siamo in modalità edit o copy, restituisco tutti i dati ricevuti dall'api
    (async () => {
      setLoading(true);
      const response = await medPrest({
        crud: "r",
        idMedint: context?.doctorAgenda?.medico?.[0]?.IDMEDICO ?? "",
        idMedprest: row?.IdMedPrest,
      }).then((data: any) => {
        setLoading(false);
        return data;
      });

      // estisco il caso in cui la chiamata non restituisce niente
      const api = response?.data.dati[0] as MedPrestApiRow | undefined;
      if (!api) {
        methods.reset({ ...methods.getValues(), medico: medicoFullName });
        return;
      }

      methods.reset({
        ...methods.getValues(),
        ...mapApiToForm(api, medicoFullName),
      });

      uploadCodiciSiss();
    })();
  }, [open, mode, row?.IdMedPrest, context?.doctorAgenda, methods]);

  useEffect(() => {
    if (!open) return;

    const datiForm = methods.getValues();

    console.log({ datiForm });
  }, [open, methods]);

  //Se raggruppabile non è abilitato, svuoto tipoRaggruppamento per non inviare dati al form
  useEffect(() => {
    if (!raggruppabile) {
      methods.setValue("flagRaggruppabile", false);
    }
  }, [raggruppabile, methods]);

  const readMedprst = async () => {
    setLoading(true);
    try {
      const response = await medprstgps({
        crud: "r",
        medprest_prgint: row?.IdMedPrest,
      });

      if (response.data.esito === "OK") {
        setSospesaFound(true);

        const tempObj = response.data.dati.medprstgps;

        methodsMedPresSosp?.setValue(
          "note",
          tempObj.MEDPRSTGPS_NOTE_SOSPENSIONE,
        );
        methodsMedPresSosp?.setValue(
          "dataSospensione",
          dayjs(tempObj.MEDPRSTGPS_DATA_SOSPENSIONE),
        );
        methodsMedPresSosp?.setValue(
          "sospesa",
          tempObj.MEDPRSTGPS_SW_SOSPESA === "0" ? false : true,
        );
      } else {
        setSospesaFound(false);
        methodsMedPresSosp.reset({
          sospesa: false,
          dataSospensione: dayjs(),
          note: "",
        });
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    } finally {
      setShowMedpresSospensioniModal(true);
      setLoading(false);
    }
  };

  const [codiciSiss, setCodiciSiss] = useState<ResponseRicAnaPre[]>([]);

  // funzion per valorizzare l'automcomplete dei codiciSiss
  const uploadCodiciSiss = async () => {
    const tempObj: PayloadRicAnaPre = {
      aziendaPrenotazione: "MEDITEL",
      dataRif: dayjs().format("YYYY-MM-DD"),
      codSpecia: methods.getValues("codiceSpecia"),
      codAnapre: methods.getValues("codiceAnapre"),
      codAnapres: methods.getValues("codAnapreSost"),
      testValidita: "S",
      flDisattivati: "S",
    };

    try {
      await ricAnapres(tempObj).then((response: any) => {
        const mapDati: ResponseRicAnaPre[] =
          response?.data?.dati?.codiciSISS?.map((e: ResponseRicAnaPre) => {
            return {
              BRANCA: e.BRANCA ?? "",
              CODANAPRE: e.CODANAPRE ?? "",
              CODANAPRES: e.CODANAPRES ?? "",
              CODSPECIA: e.CODSPECIA ?? "",
              DATAFINE: e.DATAFINE ?? "",
              DATAINIZIO: e.DATAINIZIO ?? "",
              DESANAPRE: e.DESANAPRE ?? "",
              DESANAPRES: e.CODANAPRES ?? "",
              NOMENCLATORE: e.NOMENCLATORE ?? "",
              SWDISATTIVATO: e.SWDISATTIVATO ?? "",
            } as ResponseRicAnaPre;
          });
        console.log({ mapDati });

        setCodiciSiss(mapDati);
      });
    } catch (error) {
      console.error("Errore sull'invio del payload di medpresl:", error);
    }
  };

  // handler invocato all'apertura delle modali dei distretti e delle metodiche
  const promisesHandlerAttrGp = async () => {
    setLoading(true);
    try {
      await Promise.all([
        readDistretti(),
        readMetodiche(),
        readDefaultDistretti(),
        readDefaultMetodiche(),
      ]).finally(() => {
        setLoading(false);
        setShowMedpresDistretto(true);
      });
    } catch (error) {
      console.log("Errore sulla promise handler");
    }
  };

  const readDefaultDistretti = async () => {
    try {
      const response = await ricPrestgpd({
        // anapre_prgint: methods.getValues("idAnapre"),
        anapre_prgint: "963",
      });

      const tempObj = response.data.dati.prestgpd;
      console.log({ tempObj });

      if (tempObj.messaggio === "nessun record trovato") {
        setListDistDefault(null);
        return;
      }
      if (Array.isArray(tempObj)) {
        const tempMapped: DistrDefaultRow[] = tempObj.map(
          (e: DistrDefaultRow, index: number) => {
            return {
              id: index,
              GPDISTR_CODICE: e.GPDISTR_CODICE,
              PRESTGPD_OP_LOGICO: e.PRESTGPD_OP_LOGICO,
              DECODE_GPDISTR_GPDISTR_DESCRIZIONE:
                e.DECODE_GPDISTR_GPDISTR_DESCRIZIONE,
              DECODE_ANAPRES_SPECIA_CODICE: e.DECODE_ANAPRES_SPECIA_CODICE,
              DECODE_ANAPRES_ANAPRES_SISS_COD:
                e.DECODE_ANAPRES_ANAPRES_SISS_COD,
              DECODE_ANAPRES_ANAPRE_COD: e.DECODE_ANAPRES_ANAPRE_COD,
              DECODE_ANAPRE_ANAPRE_DESCR: e.DECODE_ANAPRE_ANAPRE_DESCR,
            };
          },
        );
        setListDistDefault(tempMapped);
        return tempObj;
      } else {
        console.log("nessun dato trovato", response.data);
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    }
  };

  const readDistretti = async () => {
    try {
      const response = await ricMedPrstGpd({
        medprest_prgint: row?.IdMedPrest,
      });

      const tempObj = response.data.dati.medprstgpd;
      // il valore "esito" è sempre OK.. va confrontato con il valore di "messaggio"..
      if (tempObj.messaggio === "nessun record trovato") {
        setListDistAssoc(null);
        return;
      }
      if (Array.isArray(tempObj)) {
        const tempMapped: DistrAssRow[] = tempObj.map(
          (e: DistrAssRow, index: number) => {
            return {
              id: index,
              MEDPRSTGPD_PRGINT: e.MEDPRSTGPD_PRGINT,
              MEDPREST_PRGINT: e.MEDPREST_PRGINT,
              ANAPRES_SISS_COD: e.ANAPRES_SISS_COD,
              GPDISTR_CODICE: e.GPDISTR_CODICE,
              MEDPRSTGPD_SW_DISABILITATO: e.MEDPRSTGPD_SW_DISABILITATO,
              MEDPRSTGPD_PID_ACU: e.MEDPRSTGPD_PID_ACU,
              MEDPRSTGPD_TIMESTAMP: e.MEDPRSTGPD_TIMESTAMP,
              MEDPRSTGPD_OP_LOGICO: e.MEDPRSTGPD_OP_LOGICO,
              MEDPRSTGPD_DT_INI_VAL: e.MEDPRSTGPD_DT_INI_VAL,
              MEDPRSTGPD_DT_FINE_VAL: e.MEDPRSTGPD_DT_FINE_VAL,
              DECODE_GPDISTR_GPDISTR_DESCRIZIONE:
                e.DECODE_GPDISTR_GPDISTR_DESCRIZIONE,
              DECODE_MEDPREST_SPECIA_CODICE: e.DECODE_MEDPREST_SPECIA_CODICE,
              DECODE_MEDPREST_ANAPRE_COD: e.DECODE_MEDPREST_ANAPRE_COD,
              DECODE_ANAPRES_ANAPRES_SISS_DESCR:
                e.DECODE_ANAPRES_ANAPRES_SISS_DESCR,
            };
          },
        );
        setListDistAssoc(tempMapped);
        return tempObj;
      } else {
        console.log("nessun dato trovato", response.data);
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    }
  };

  const readDefaultMetodiche = async () => {
    try {
      const response = await ricPrestgpm({
        // anapre_prgint: methods.getValues("idAnapre"),
        anapres_prgint: "69",
      });

      const tempObj = response.data.dati.prestgpm;
      console.log({ tempObj });

      if (tempObj.messaggio === "nessun record trovato") {
        setListMetodDefault(null);
        return;
      }
      if (Array.isArray(tempObj)) {
        const tempMapped: MethodsDefaultRow[] = tempObj.map(
          (e: MethodsDefaultRow, index: number) => {
            return {
              id: index,
              DECODE_ANAPRES_ANAPRE_COD: e.DECODE_ANAPRES_ANAPRE_COD,
              DECODE_ANAPRES_ANAPRES_SISS_COD: e.DECODE_ANAPRES_ANAPRES_SISS_COD,
              GPMETOD_CODICE: e.GPMETOD_CODICE,
              DECODE_ANAPRES_SPECIA_CODICE: e.DECODE_ANAPRES_SPECIA_CODICE,
              DECODE_GPMETOD_GPMETOD_DESCRIZIONE: e.DECODE_GPMETOD_GPMETOD_DESCRIZIONE
            };
          },
        );
        setListMetodDefault(tempMapped);
        return tempObj;
      } else {
        console.log("nessun dato trovato", response.data);
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    }
  };

  const readMetodiche = async () => {
    try {
      const response = await ricMedprstgpm({
        medprest_prgint: row?.IdMedPrest,
      });
      const tempObj = response.data.dati.medprstgpm;
      // il valore "esito" è sempre OK.. va confrontato con il valore di "messaggio"..
      if (response.data.messaggio === "nessun dato trovato") {
        setListMetodAss(null);
        return;
      }
      if (Array.isArray(tempObj)) {
        const tempMapped: MetodAssRow[] = tempObj.map(
          (e: MetodAssRow, index: number) => {
            return {
              id: index,
              MEDPRSTGPM_PRGINT: e.MEDPRSTGPM_PRGINT,
              MEDPREST_PRGINT: e.MEDPREST_PRGINT,
              ANAPRES_SISS_COD: e.ANAPRES_SISS_COD,
              GPMETOD_CODICE: e.GPMETOD_CODICE,
              MEDPRSTGPM_SW_DISABILITATO: e.MEDPRSTGPM_SW_DISABILITATO,
              MEDPRSTGPM_PID_ACU: e.MEDPRSTGPM_PID_ACU,
              MEDPRSTGPM_TIMESTAMP: e.MEDPRSTGPM_TIMESTAMP,
              MEDPRSTGPM_DT_INI_VAL: e.MEDPRSTGPM_DT_INI_VAL,
              MEDPRSTGPM_DT_FINE_VAL: e.MEDPRSTGPM_DT_FINE_VAL,
              DECODE_GPMETOD_GPMETOD_DESCRIZIONE:
                e.DECODE_GPMETOD_GPMETOD_DESCRIZIONE,
              DECODE_MEDPREST_SPECIA_CODICE: e.DECODE_MEDPREST_SPECIA_CODICE,
              DECODE_MEDPREST_ANAPRE_COD: e.DECODE_MEDPREST_ANAPRE_COD,
              DECODE_ANAPRES_ANAPRES_SISS_DESCR:
                e.DECODE_ANAPRES_ANAPRES_SISS_DESCR,
            };
          },
        );
        setListMetodAss(tempMapped);
        return tempObj;
      } else {
        console.log("nessun dato trovato", response.data);
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    }
  };

  const handleSubmitSospesa = async () => {
    let tempCrud;

    // controllo sull'useState aggiornato se in call trovato record della
    // sospesa sul db e della spunta se sempre presente
    if (sospesaFound) {
      if (methodsMedPresSosp?.getValues("sospesa") === true) {
        tempCrud = "u";
      } else {
        tempCrud = "d";
      }
    } else {
      tempCrud = "c";
    }

    // definizione dell'object in caso di delete oppure create/update
    const tempObj =
      tempCrud === "d"
        ? { crud: tempCrud, medprest_prgint: row?.IdMedPrest }
        : {
            crud: tempCrud,
            dati: {
              medprstgps: {
                MEDPREST_PRGINT: row?.IdMedPrest ?? "",
                MEDPRSTGPS_DATA_SOSPENSIONE:
                  dayjs(methodsMedPresSosp.getValues("dataSospensione")).format(
                    "YYYY-MM-DD",
                  ) ?? "",
                MEDPRSTGPS_NOTE_SOSPENSIONE:
                  methodsMedPresSosp?.getValues("note") ?? "",
                MEDPRSTGPS_SW_SOSPESA:
                  methodsMedPresSosp?.getValues("sospesa") === true ? "1" : "0",
              },
            },
          };

    try {
      const response = await medprstgps(tempObj);
      setMessage(response.data.messaggio);
      if (response.data.esito === "OK") {
        // controllo e reset in caso di cancellazione per renderla eventualmente reinseribile
        if (tempCrud === "d") {
          setSospesaFound(false);
        } else {
          setSospesaFound(true);
        }
        setSeverity("success");
      }
      if (response.data.esito === "KO") {
        setSeverity("error");
      }
    } catch (error) {
      console.error("Errore durante la post delle sospese", error);
    } finally {
      setAllert(true);
      setTimeout(() => {
        setAllert(false);
        setShowMedpresSospensioniModal(false);
      }, 3000);
    }
  };

  const alertProps = {
    showAllert: allert,
    message: message,
    setAllert: setAllert,
    setSeverity: setSeverity,
    setMessage: setMessage,
    severity: severity,
    fn: fn,
    setFn: setFn,
  };

  return (
    <>
      {/* ALLERT */}
      <AllertMessage
        showAllert={allert}
        message={message}
        setShowAllert={setAllert}
        severity={severity}
        // nella seconda parte c'è da capire dove spostare la logica
        submitFunction={
          fn
            ? fn
            : () => {
                if (methodsMedPresl.getValues("crud") === "r") {
                  return onAlertConfirm();
                } else if (methodsMedPresl.getValues("crud") === "d") {
                  return handlePayloadMEDPRESL();
                } else {
                  return;
                }
              }
        }
      />

      {/* MODALE DEL FORM RELATIVO IMPORTI */}
      <MedPresl
        showModale={showMedpreslModal}
        setShowModale={setShowMedpreslModal}
        submitFunction={handlePayloadMEDPRESL}
        methods={methodsMedPresl}
      />

      {/* MODALE DEL FORM RELATIVO SOSPENSIONI PRESTAZIONI */}
      <SospensioneModal
        showModale={showMedpresSospensioniModal}
        setShowModale={setShowMedpresSospensioniModal}
        submitFunction={handleSubmitSospesa}
        methods={methodsMedPresSosp}
        sospesaFound={sospesaFound}
      />

      {/* MODALE DEL FORM DISTRETTO PRESTAZIONI */}
      <AttributiGpModal
        methods={methodsMedPresSosp}
        rowData={row}
        methodsMedDettaglio={methods}
        methodsDistr={methodsDistr}
        methodsMetod={methodsMetod}
        showModale={showMedpresDistretto}
        setShowModale={setShowMedpresDistretto}
        submitFunction={handlePayloadSospensione}
        listDistAssoc={listDistAssoc}
        listMetodAssoc={listMetodAss}
        listDistDefault={listDistDefault}
        listMetodDefault={listMetodDefault}
        codiciSiss={codiciSiss}
        alertProps={alertProps}
        promisesHandlerAttrGp={promisesHandlerAttrGp}
        loading={loading}
      />

      {/* LOADING */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1300,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2,
            minHeight: 38,
            "& .MuiTabs-indicator": {
              backgroundColor: BLUE,
              height: 3,
              borderRadius: 3,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              px: 0,
              mr: 3,
              fontFamily: "Montserrat",
              fontWeight: "bold",
              color: "#9e9e9e",
              transition: "color .2s ease",
            },
            "& .MuiTab-root.Mui-selected": {
              color: BLUE,
            },
          }}
        >
          <Tab value="dettaglio" label="Dettaglio" />
          <Tab value="mosa" label="Dati Mosa GP" />
        </Tabs>
        <FormProvider {...methods}>
          {/* TAB 1 - Dettaglio */}
          <Box hidden={tab !== "dettaglio"}>
            <Grid container spacing={2}>
              {/* MEDICO */}
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="medico"
                  label="Medico"
                  control={methods.control}
                  fullWidth
                  disabled
                />
              </Grid>

              {/* SPECIALITA' */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  name="codiceSpecia"
                  label="Specialità"
                  disabled={mode !== "create"}
                  control={methods.control}
                  options={context?.specialitiesSPECIALITA ?? []}
                  getOptionLabel={(s: any) => `${s.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.SPECIALITA).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => {
                    return String(opt?.SPECIALITA ?? "");
                  }}
                />
              </Grid>

              {/* PRESTAZIONE */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  name="codiceAnapre"
                  label="Prestazione"
                  disabled={mode !== "create"}
                  control={methods.control}
                  options={
                    mode === "create"
                      ? prestazioniFiltratePerSpecialita
                      : (context?.performancePRESTAZIONI ?? [])
                  }
                  getOptionLabel={(p: any) => `${p.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.PRESTAZIONE).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.PRESTAZIONE ?? "")}
                />
              </Grid>

              {/* TIPOLOGIA */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  name="codiceTpgpre"
                  label="Tipologia"
                  control={methods.control}
                  options={context?.typologiesTIPOLOGIE ?? []}
                  getOptionLabel={(t: any) => `${t.DESCRIZIONE ?? ""}`.trim()}
                  mapIn={(code, opts) =>
                    opts.find(
                      (o: any) =>
                        String(o.CODICE).trim() === String(code).trim(),
                    ) ?? null
                  }
                  mapOut={(opt: any) => String(opt?.CODICE ?? "")}
                />
              </Grid>

              {/* COLONNA 1 */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} sx={{ width: "100%" }}>
                    <Stack direction={"column"} spacing={2}>
                      {/* DURATA */}
                      <RHFTextField
                        name="durataMin"
                        label="Durata"
                        control={methods.control}
                        fullWidth
                      />

                      {/* ATTESA APPLICAZIONE */}
                      <RHFTextField
                        name="attesaMin"
                        label="Attesa applicazione"
                        control={methods.control}
                        fullWidth
                      />

                      {/* NUMERO MAX PREST */}
                      <RHFTextField
                        name="nMaxPrest"
                        label="Num. max prestazioni"
                        control={methods.control}
                        fullWidth
                      />

                      {/* VALIDO AL */}
                      <RHFDateTimePicker
                        name="dataFineValidita"
                        label="Valido fino al"
                        control={methods.control}
                        dateOnly
                        fullWidth
                      />

                      {/* ACCETTAZIONE AMBULATORIALE */}
                      <RHFCheckbox
                        name="flagAccettazione"
                        label="Solo in Accettazione Ambulatoriale"
                        control={methods.control}
                      />
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ width: "100%", height: "100%" }}
                  >
                    <Stack
                      direction={"column"}
                      sx={{
                        display: "flex",
                        borderRadius: "4px",
                        border: "1px solid #CCC",
                        padding: 2,
                        height: "auto",
                      }}
                    >
                      <Grid container>
                        <Grid item xs={10}>
                          <Typography
                            fontSize={"small"}
                            sx={{ fontWeight: "bold", paddingBottom: 1 }}
                          >
                            Importi di Listino del Medico/Risorsa
                          </Typography>
                          <StyledDataGrid
                            rows={rowsImporti ?? []}
                            columns={prestazioniCols}
                            getRowId={(r) => r.id}
                            onRowClick={(event: any) => {
                              if (event?.row?.id === selectedImporto?.id) {
                                setSelectedImporto(undefined);
                              } else {
                                setSelectedImporto(event?.row);
                              }
                            }}
                            sortingOrder={["desc", "asc", null]}
                            sx={{
                              height: 200,
                              '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                                {
                                  display: "none",
                                },
                              "& .MuiDataGrid-row.Mui-selected": {
                                backgroundColor: "#e3f2fd",
                              },
                              "& .MuiDataGrid-row.Mui-selected:hover": {
                                backgroundColor: "#bbdefb",
                              },
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          display={"flex"}
                          justifyContent={"space-around"}
                        >
                          <Stack direction={"column-reverse"}>
                            <Button
                              variant="outlinedError"
                              disabled={!selectedImporto}
                              sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                p: 0,
                                borderRadius: 1,
                                mt: 1,
                              }}
                              onClick={() => {
                                methodsMedPresl.reset({
                                  crud: "d",
                                  idMedpresl: selectedImporto?.idMedPresl,
                                  idMedprest: selectedImporto?.idMedPrest,
                                });
                                setMessage(
                                  "Eliminare l'importo selezionato dal listino?",
                                );
                                setSeverity("warning");
                                setAllert(true);
                              }}
                            >
                              {" "}
                              <DeleteIcon />{" "}
                            </Button>
                            <Button
                              variant="outlinedCustom"
                              disabled={!selectedImporto}
                              sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                p: 0,
                                borderRadius: 1,
                                mt: 1,
                              }}
                              onClick={() => {
                                methodsMedPresl.reset({
                                  crud: "c",
                                  dataIniValidita: dayjs(
                                    selectedImporto?.inizio,
                                    "DD/MM/YYYY",
                                  ),
                                  importoListino: selectedImporto?.listino,
                                  importoEnte: selectedImporto?.ente,
                                  importoPaz: selectedImporto?.paziente,
                                });
                                setShowMedpreslModal(true);
                              }}
                            >
                              {" "}
                              <CopyFromDoctorIcon />{" "}
                            </Button>
                            <Button
                              variant="outlinedCustom"
                              disabled={!selectedImporto}
                              sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                p: 0,
                                borderRadius: 1,
                                mt: 1,
                              }}
                              onClick={() => {
                                methodsMedPresl.reset({
                                  crud: "u",
                                  idMedpresl: selectedImporto?.idMedPresl,
                                  dataIniValidita: dayjs(
                                    selectedImporto?.inizio,
                                    "DD/MM/YYYY",
                                  ),
                                  importoListino: selectedImporto?.listino,
                                  importoEnte: selectedImporto?.ente,
                                  importoPaz: selectedImporto?.paziente,
                                });
                                setShowMedpreslModal(true);
                              }}
                            >
                              {" "}
                              <ModifyIcon />{" "}
                            </Button>
                            <Button
                              variant="outlinedCustom"
                              sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                p: 0,
                                borderRadius: 1,
                                mt: 1,
                              }}
                              onClick={() => {
                                methodsMedPresl.reset({
                                  crud: "c",
                                  dataIniValidita: dayjs(),
                                });
                                setShowMedpreslModal(true);
                              }}
                            >
                              {" "}
                              <AddIcon />{" "}
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>

              {/* NOTA 1 */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="nota1"
                  rules={{ required: "Il campo note è obbligatorio" }}
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      autoComplete
                      autoHighlight
                      size="small"
                      options={context?.dettagliNotePANOTET || []}
                      getOptionLabel={(option) =>
                        `[${option.codice}] ${option.descrizione}` || ""
                      }
                      onChange={(_, data) => {
                        console.log({ data });

                        field.onChange(data);
                        methods.setValue("nota1", {
                          codice: data?.codice,
                          descrizione: data?.descrizione,
                        });
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.codice === value.codice
                      }
                      value={field.value || null}
                      renderInput={(params) => (
                        <Tooltip title={methods.getValues("nota1").descrizione}>
                          <TextField
                            {...params}
                            placeholder="Selezionare"
                            label="Nota 1"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        </Tooltip>
                      )}
                      clearOnEscape
                    />
                  )}
                />
              </Grid>

              {/* NOTA 2 */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="nota2"
                  control={methods.control}
                  rules={{ required: "Il campo note è obbligatorio" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      autoComplete
                      autoHighlight
                      size="small"
                      options={context?.dettagliNotePANOTET || []}
                      getOptionLabel={(option) =>
                        `[${option.codice}] ${option.descrizione}` || ""
                      }
                      onChange={(_, data) => {
                        console.log({ data });

                        field.onChange(data);
                        methods.setValue("nota2", {
                          codice: data?.codice,
                          descrizione: data?.descrizione,
                        });
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.codice === value.codice
                      }
                      value={field.value || null}
                      renderInput={(params) => (
                        <Tooltip title={methods.getValues("nota2").descrizione}>
                          <TextField
                            {...params}
                            placeholder="Selezionare"
                            label="Nota 2"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        </Tooltip>
                      )}
                      clearOnEscape
                    />
                  )}
                />
              </Grid>

              {/* AGENDA */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  name="codPgmedt"
                  rules={{ required: "Il campo agenda è obbligatorio" }}
                  label="Agenda"
                  control={methods.control}
                  options={listPgmedt}
                  keyField="pkey"
                  getOptionLabel={(o: any) =>
                    `${o?.["cod.programma"] ?? ""} - ${o?.descrizione ?? ""}`.trim()
                  }
                  mapIn={(value, opts) =>
                    opts.find(
                      (o: any) => String(o?.pkey ?? "") === String(value ?? ""),
                    ) ?? null
                  }
                  mapOut={(opt) => {
                    if (!opt) return "";
                    if (Array.isArray(opt)) return "";
                    return String((opt as any).pkey ?? "");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  borderRadius: "4px",
                  border: "1px solid #CCC",
                  marginTop: 2,
                  marginLeft: 2,
                }}
              >
                <Grid container paddingBottom={2} paddingRight={2}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "bold", paddingBottom: 1 }}>
                      Sostituzione dati prestazione prenotazione in accettazione
                    </Typography>
                  </Grid>
                  <Stack direction={"row"} gap={2} width={"100%"}>
                    <RHFAutocomplete
                      name="codAnapreSost"
                      label="Prestazione"
                      control={methods.control}
                      fullWidth
                      options={context?.performancePRESTAZIONI ?? []}
                      keyField="IDPRESTAZIONE"
                      getOptionLabel={(p: any) =>
                        `${p.CODICEPRESTAZIONE ?? ""} ${p.DESCRIZIONE ?? ""}`.trim()
                      }
                      mapIn={(code, opts) =>
                        opts.find(
                          (o: any) =>
                            String(
                              o?.CODICEPRESTAZIONE ??
                                o?.PRESTAZIONE ??
                                o?.CODICEANAPRE ??
                                "",
                            ).trim() === String(code ?? "").trim(),
                        ) ?? null
                      }
                      mapOut={(opt: any) =>
                        String(
                          opt?.CODICEPRESTAZIONE ??
                            opt?.PRESTAZIONE ??
                            opt?.CODICEANAPRE ??
                            "",
                        ).trim()
                      }
                    />

                    <RHFAutocomplete
                      control={methods.control}
                      name="codMedintSost"
                      label="Medico"
                      fullWidth
                      options={context?.doctorsData ?? []}
                      keyField="IDMEDICO"
                      getOptionLabel={(d: any) =>
                        ` ${
                          d.COGNOMEMEDICO ?? d.cognome ?? ""
                        } ${d.NOMEMEDICO ?? d.nome ?? ""}`.trim()
                      }
                      mapIn={(code, opts) =>
                        opts.find(
                          (o: any) =>
                            String(
                              o.CODICEMEDICO ?? o?.["codice medico"] ?? "",
                            ).trim() === String(code ?? "").trim(),
                        ) ?? null
                      }
                      mapOut={(opt: any) =>
                        String(
                          opt?.CODICEMEDICO ?? opt?.["codice medico"] ?? "",
                        ).trim() || ""
                      }
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* TAB 2 - Dati Mosa GP */}
          <Box hidden={tab !== "mosa"}>
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                p: 2,
                mt: 1,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* ETÀ MIN */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="etaMin"
                    label="Età min."
                    placeholder="Placeholder"
                    control={methods.control}
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* ETÀ MAX */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="etaMax"
                    label="Età max."
                    placeholder="Placeholder"
                    control={methods.control}
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* SESSO + TEMPO PREPARAZIONE  */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontSize: 14, minWidth: 60 }}>
                      Sesso
                    </Typography>

                    <RHFRadioGroup
                      name="genere"
                      control={methods.control}
                      options={[
                        { label: "Maschile", value: "M" },
                        { label: "Femminile", value: "F" },
                        { label: "Nessun Controllo", value: "" },
                      ]}
                      row
                      sx={{
                        "& .MuiFormControlLabel-root": { mr: 2 },
                        "& .MuiTypography-root": { fontSize: 14 },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="tempoPreparazione"
                    label="Tempo di preparazione"
                    placeholder="Placeholder"
                    control={methods.control}
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* RAGGRUPPABILE + TIPO RAGGRUPPAMENTO (stessa riga) */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <RHFCheckbox
                      name="flagRaggruppabile"
                      label="Raggruppabile"
                      control={methods.control}
                    />

                    <Box sx={{ flex: 1 }}>
                      <RHFAutocomplete
                        disabled={!raggruppabile}
                        name="codiceRaggruppamento"
                        label="Tipo Raggruppamento"
                        control={methods.control}
                        fullWidth
                        options={context?.raggruppamentoPARAGGGP ?? []}
                        keyField="id"
                        getOptionLabel={(d: FormattedRagggpDataObject) =>
                          `[${
                            d.codice ?? "N/a"
                          }] ${d.descrizione ?? "N/a descrizione"}`.trim()
                        }
                        mapIn={(code, opts) =>
                          opts.find(
                            (o: FormattedRagggpDataObject) =>
                              String(o?.codice).trim() === String(code).trim(),
                          ) ?? null
                        }
                        mapOut={(opt: any) =>
                          String(opt?.codice ?? opt?.codice ?? "").trim() || ""
                        }
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Colonna destra vuota per mantenere la griglia come da design */}
                <Grid item xs={12} md={6} />

                {/* ESCLUDI DA GP */}
                <Grid item xs={12}>
                  <RHFCheckbox
                    name="flagEscludiGP"
                    label="Escludi da GP"
                    control={methods.control}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button variant="outlinedError" onClick={onClose}>
              Uscita
            </Button>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlinedCustom"
                onClick={async () => readMedprst()}
              >
                Sospensione
              </Button>
              <Button
                variant="outlinedCustom"
                onClick={async () => promisesHandlerAttrGp()}
              >
                Attr. GP
              </Button>
            </Box>

            <Button variant="containedCustom" onClick={askConfirmMedPrest}>
              Conferma
            </Button>
          </Box>
        </FormProvider>
      </>
    </>
  );
};

export default MedPrest;
